/**
 * Desktop Notifications & Sound Utility
 */

/**
 * Request permission for desktop notifications
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.warn('This browser does not support desktop notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
};

/**
 * Play notification sound
 * @param {string} soundType - Type of sound (default, chime, bell, ping, alert)
 */
export const playNotificationSound = (soundType = 'default') => {
  const publicSiteUrl = (process.env.REACT_APP_PUBLIC_SITE_URL || '').replace(/\/$/, '');
  const base = publicSiteUrl || '';
  const audio = new Audio(`${base}/assets/sounds/${soundType}.mp3`);
  audio.volume = 0.5;
  
  audio.play().catch(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      const frequencies = {
        'default': 800,
        'chime': 1200,
        'bell': 600,
        'ping': 1000,
        'alert': 900
      };
      
      oscillator.frequency.value = frequencies[soundType] || 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (err) {
      console.warn('Could not play notification sound:', err);
    }
  });
};

/**
 * Show desktop notification with sound
 * @param {string} title - Notification title
 * @param {object} options - Notification options
 * @param {boolean} enableSound - Whether to play sound
 * @param {string} soundType - Type of sound to play
 */
export const showDesktopNotification = async (title, options = {}, enableSound = true, soundType = 'default') => {
  const hasPermission = await requestNotificationPermission();
  
  if (!hasPermission) {
    console.warn('Notification permission not granted');
    return null;
  }

  if (enableSound) {
    playNotificationSound(soundType);
  }

  const notification = new Notification(title, {
    icon: '/favicon.ico',
    badge: '/favicon.ico',
    ...options
  });

  setTimeout(() => {
    notification.close();
  }, 10000);

  return notification;
};

/**
 * Show notification for new reservation
 * @param {object} reservation - Reservation object
 * @param {object} settings - User settings
 */
export const showNewReservationNotification = async (reservation, settings = {}) => {
  if (!settings.desktop_notifications_enabled) {
    return;
  }

  const serviceName = reservation.nazov_sluzby || 'Neznáma služba';
  const customerName = reservation.meno || 'Neznámy zákazník';
  const date = new Date(reservation.datum_rezervacie).toLocaleDateString('sk-SK');
  const time = reservation.cas_rezervacie;

  await showDesktopNotification(
    '🔔 Nová rezervácia!',
    {
      body: `${customerName}\n${serviceName}\n${date} o ${time}`,
      tag: `reservation-${reservation.id}`,
      requireInteraction: false
    },
    true,
    settings.notification_sound || 'default'
  );
};

/**
 * Show notification for pending reservations count
 * @param {number} count - Number of pending reservations
 * @param {object} settings - User settings
 */
export const showPendingReservationsNotification = async (count, settings = {}) => {
  if (!settings.desktop_notifications_enabled || count === 0) {
    return;
  }

  await showDesktopNotification(
    '⏳ Rezervácie čakajúce na potvrdenie',
    {
      body: `Máte ${count} ${count === 1 ? 'rezerváciu' : count < 5 ? 'rezervácie' : 'rezervácií'} čakajúcich na potvrdenie`,
      tag: 'pending-reservations',
      requireInteraction: false
    },
    true,
    settings.notification_sound || 'default'
  );
};
